import {
	AfterViewInit,
	Component,
	inject,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import {
	FormularioRelatorio,
	INPUTS_RELATORIOS_PCDS,
	InstanciaRelatorio,
} from '@home/submodulos/dados-meteorologicos/interfaces/tipos-relatorios';
import {
	DadosEvapotranspiracao,
	Relatorios,
} from '@home/submodulos/dados-meteorologicos/interfaces/tabela-relatorio';
import { ToastrService } from 'ngx-toastr';
import { RelatoriosService } from '@home/submodulos/dados-meteorologicos/services/relatorios.service';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import { format, formatISO } from 'date-fns';
import { Validators } from '@angular/forms';
import { isNotNuloOuUndefined, renderNumeroBR } from '@utils';
import { DateTimeUtils } from '@utils/datetime-util';
import { ADTColumns } from 'angular-datatables/src/models/settings';
import { PublicTableComponent } from '@componentes/public-table/public-table.component';

import {
	exportarCSV,
	exportarPDF,
	exportarTXT,
	exportarXLSX,
} from '@home/submodulos/dados-meteorologicos/utils/exportacao-tabelas';
import { Select } from '@layout/interfaces/select';
import { corrigeDuplicacaoNome } from '@home/submodulos/dados-meteorologicos/utils';
import { filter, Subscription } from 'rxjs';
import moment from 'moment';
import {
	PERIODO_MAXIMO_EM_ANOS_GRAFICO_PCD,
	PERIODO_MAXIMO_EM_DIAS_GRAFICO_PCD,
	PERIODO_MAXIMO_EM_MESES_GRAFICO_PCD,
} from '../grafico-variaveis-meteorologicas/grafico-variaveis-meteorologicas.component';

@Component({
	selector: 'seira-evapotranspiracao-tabela',
	templateUrl: './evapotranspiracao-tabela.component.html',
	styleUrls: ['./evapotranspiracao-tabela.component.scss'],
})
export class EvapotranspiracaoTabelaComponent
	implements OnInit, OnDestroy, InstanciaRelatorio, AfterViewInit
{
	carregando = false;
	tituloTabela = '';
	periodoAgrupamento = '';
	inputs = inject(INPUTS_RELATORIOS_PCDS);
	dadosTabelaEvapotranspiracao: DadosEvapotranspiracao[] = [];
	opcoesMunPostos: Select<string>[];
	mensagemDataMinima?: string;
	private inscricoes = new Subscription();

	descricaoRelatorio =
		'Define-se como evapotranspiração a quantidade total de água transferida para a atmosfera, resultado da soma da evaporação do solo e da transpiração das plantas de uma determinada área.';

	@ViewChild('tabelaEvapotranspiracao', { static: false })
	tabela?: PublicTableComponent;

	colunas: ADTColumns[] = [
		{
			data: 'nomeEstacao',
			title: 'Município/Posto ',
			type: 'string',
			className: 'text-left',
		},
		{
			data: 'dataColeta',
			title: 'Data',
			type: 'string',
			className: 'text-center',
		},
		{
			data: 'evapotranspiracaoPotencial',
			title: 'Evapotranspiração potencial (mm)',
			type: 'number',
			className: 'text-center',
			render: value => renderNumeroBR(value, 2),
		},
	];

	botoesDeExportacao: GroupButton[] = [
		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			onClick: () =>
				exportarPDF({
					resultados: this.colunas,
					tabela: this.tabela,
					periodoAgrupamento: this.periodoAgrupamento,
					tituloTabela: this.tituloTabela,
				}),
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () =>
				exportarCSV({
					tabela: this.tabela!,
					periodoAgrupamento: this.periodoAgrupamento,
					tituloTabela: this.tituloTabela,
				}),
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () =>
				exportarTXT({
					tabela: this.tabela!,
					periodoAgrupamento: this.periodoAgrupamento,
					titulo: this.tituloTabela,
				}),
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () =>
				exportarXLSX({
					tabela: this.tabela,
					periodoAgrupamento: this.periodoAgrupamento,
					tituloTabela: this.tituloTabela,
					colunasAEsquerda: [0],
					configsColunas: [
						{ coluna: 'evapotranspiracaoPotencial', casasDecimais: 1 },
					],
				}),
		},
	];

	constructor(
		private toastr: ToastrService,
		private relatoriosService: RelatoriosService
	) {
		const todosMunicipiosPostos = this.inputs.municipioPostos;
		this.ouvirMudancasMensagemDataMin();
		this.ouvirMudancasPeriodoBusca();
		this.ouvirMudancasMunPosto();
		this.setValidators();
		this.opcoesMunPostos = [
			{ value: '0', name: 'Estado completo' },
			...todosMunicipiosPostos,
		];
		this.periodoBusca = 'diario';
	}

	ngAfterViewInit(): void {
		this.gerarRelatorio();
	}

	private ouvirMudancasMunPosto(): void {
		this.inputs.form
			.get(FormularioRelatorio.ESTACAO)
			?.valueChanges.subscribe(munPosto => {
				if (munPosto === '0') {
					this.mensagemDataMinima = undefined;
					return;
				}
				const dto = this.inputs.datasPrimeirasColetasPCDs.find(d => {
					return d.id.toString() === munPosto;
				})!;
				if (!dto) {
					return;
				}
				this.dataInicio = this.getInicioDataByPeriodoBusca(dto.dataColeta);
				this.dataFim = this.getDataComAcrescimoByPeriodoBusca(dto.dataColeta);
			});
	}

	private ouvirMudancasMensagemDataMin(): void {
		this.inscricoes.add(
			this.inputs.triggerTrocaMensagemDataMin$
				.pipe(filter(e => isNotNuloOuUndefined(e)))
				.subscribe(mensagem => {
					this.mensagemDataMinima = mensagem;
				})
		);
	}

	private ouvirMudancasPeriodoBusca() {
		this.inscricoes.add(
			this.inputs.form
				.get(FormularioRelatorio.PERIODO_BUSCA)
				?.valueChanges.subscribe(() => {
					if (this.municipioPostoSelecionado !== '0') {
						this.atualizarDatasByDataMinima();
					}
					this.gerarRelatorio();
				})
		);
	}

	private atualizarDatasByDataMinima() {
		this.dataInicio = this.getInicioDataByPeriodoBusca(this.dataMinimaAtual);
		this.dataFim = this.getDataComAcrescimoByPeriodoBusca(this.dataInicio);
	}

	private get dataInicio(): Date {
		return this.inputs.form.get('dataInicio')?.value;
	}

	private get dataFim(): Date {
		return this.inputs.form.get('dataFim')?.value;
	}

	private get dataMinimaAtual(): Date {
		return this.inputs.datasPrimeirasColetasPCDs.find(d => {
			return d.id.toString() === this.municipioPostoSelecionado;
		})!.dataColeta;
	}

	private get municipioPostoSelecionado() {
		return this.inputs.form.get(FormularioRelatorio.ESTACAO)?.value;
	}

	private getDataComAcrescimoByPeriodoBusca(data: Date): Date {
		const mapeamento: Record<string, [number, moment.DurationInputArg2]> = {
			diario: [PERIODO_MAXIMO_EM_DIAS_GRAFICO_PCD, 'day'],
			mensal: [PERIODO_MAXIMO_EM_MESES_GRAFICO_PCD, 'month'],
			anual: [PERIODO_MAXIMO_EM_ANOS_GRAFICO_PCD, 'year'],
		};
		const [amount, unit] = mapeamento[this.periodoBusca];
		return moment(data)
			.add(amount - 1, unit)
			.toDate();
	}

	private set dataInicio(novaData: Date) {
		this.inputs.form.get('dataInicio')?.setValue(novaData);
	}

	private set dataFim(novaData: Date) {
		this.inputs.form.get('dataFim')?.setValue(novaData);
	}

	private getInicioDataByPeriodoBusca(data: Date): Date {
		const dataMoment = moment(data);
		const mapeamento: Record<string, moment.unitOfTime.StartOf> = {
			DIARIO: 'day',
			MENSAL: 'month',
			ANUAL: 'year',
		};
		const startOf = mapeamento[this.periodoBusca.toUpperCase()];
		return dataMoment.startOf(startOf).toDate();
	}

	private get periodoBusca(): string {
		return this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)?.value;
	}

	private set periodoBusca(valor: string) {
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValue(valor, { emitEvent: false });
	}

	ngOnInit() {
		this.mensagemDataMinima = this.inputs.mensagemDataMinima;
		this.inputs.form
			.get(FormularioRelatorio.ESTACAO)
			?.setValue(this.opcoesMunPostos[0].value);
		this.preencherDatas();
	}

	private preencherDatas() {
		const hoje = new Date();
		this.dataInicio = moment(hoje)
			.subtract(PERIODO_MAXIMO_EM_DIAS_GRAFICO_PCD, 'days')
			.toDate();
		this.dataFim = hoje;
	}

	ngOnDestroy() {
		this.inputs.form.get(FormularioRelatorio.DATA_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)?.clearValidators();
		this.inscricoes.unsubscribe();
	}

	setValidators() {
		this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_FIM)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValidators(Validators.required);
	}

	getFormItemValue(field: string) {
		return this.inputs.form.get(field)?.value;
	}

	gerarRelatorio() {
		let postoIds = [];
		const dataInicio = this.getFormItemValue(FormularioRelatorio.DATA_INICIO);
		const dataFim = this.getFormItemValue(FormularioRelatorio.DATA_FIM);
		this.periodoAgrupamento = this.getFormItemValue(
			FormularioRelatorio.PERIODO_BUSCA
		);
		const estacao = this.getFormItemValue(FormularioRelatorio.ESTACAO);

		if (estacao == 0) {
			postoIds = this.inputs.postos.map(posto => posto.id);
		} else {
			postoIds = [estacao];
		}

		this.tituloTabela = `Evapotranspiração - ${DateTimeUtils.formatarDataPeriodo(
			dataInicio,
			dataFim,
			this.periodoAgrupamento
		)}`;

		this.carregando = true;
		this.inputs.setLoading(true);

		this.relatoriosService
			.buscarRelatorioEvapotranspiracao(
				postoIds,
				formatISO(new Date(dataInicio)),
				formatISO(new Date(dataFim.setHours(dataFim.getHours() - 3)))
			)
			.subscribe({
				next: evapotranspiracao => {
					this.dadosTabelaEvapotranspiracao =
						this.padronizarResultado(evapotranspiracao);
					this.relatoriosService.atualizaVisibilidadeBotoesExportacao(
						evapotranspiracao
					);
				},
				error: err => {
					this.toastr.error('Ocorreu um erro ao gerar o relatório', err);
					this.carregando = false;
					this.inputs.setLoading(false);
				},
				complete: () => {
					this.carregando = false;
					this.inputs.setLoading(false);
				},
			});
	}

	padronizarResultado(
		relatorios: Relatorios<DadosEvapotranspiracao>[]
	): DadosEvapotranspiracao[] {
		const resultado: DadosEvapotranspiracao[] = [];

		if (this.periodoAgrupamento === 'diario') {
			relatorios.forEach(relatorio => {
				relatorio.data.forEach(dado =>
					resultado.push({
						nomeEstacao: corrigeDuplicacaoNome(
							`${relatorio.municipio}/${dado.nomeEstacao}`
						),
						dataColeta: format(new Date(dado.dataColeta), 'dd/MM/yyyy'),
						evapotranspiracaoPotencial: dado.evapotranspiracaoPotencial,
					})
				);
			});

			return resultado;
		}

		const agrupado = new Map<
			string,
			{ somaEvapotranspiracao: number; count: number }
		>();

		relatorios.forEach(relatorio => {
			relatorio.data.forEach(dado => {
				const data = new Date(dado.dataColeta);
				let chaveAgrupamento = '';

				if (this.periodoAgrupamento === 'mensal') {
					chaveAgrupamento = format(data, 'MM/yyyy');
				} else if (this.periodoAgrupamento === 'anual') {
					chaveAgrupamento = format(data, 'yyyy');
				}

				const chave = `${relatorio.municipio}/${dado.nomeEstacao}-${chaveAgrupamento}`;

				if (!agrupado.has(chave)) {
					agrupado.set(chave, { somaEvapotranspiracao: 0, count: 0 });
				}

				const valorAtual = agrupado.get(chave)!;
				if (isNotNuloOuUndefined(dado.evapotranspiracaoPotencial)) {
					valorAtual.somaEvapotranspiracao += dado.evapotranspiracaoPotencial;
				}
				valorAtual.count += 1;
			});
		});

		agrupado.forEach((valor, chave) => {
			const [nomeEstacao, dataColeta] = chave.split('-');
			resultado.push({
				nomeEstacao: corrigeDuplicacaoNome(nomeEstacao),
				dataColeta,
				evapotranspiracaoPotencial: valor.somaEvapotranspiracao,
			});
		});

		return resultado;
	}
}
